import { InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { useEffect, useRef, useState } from "react";
import { MY_PROVIDER_DATA_SCOPES } from "../InitializeMyProviderDataService";
import { waitForBridgeToken } from "./mpdTokenBridge";

/**
 * Auth gate for embedded routes whose APIs require an MSAL bearer token
 * (e.g. the mpd panel, which calls MyProviderData). Contrast with
 * EmbeddedAuthGate, which rides the enterprise cookie session - that session
 * cannot mint AAD tokens, so it is no help here.
 *
 * The PBS Live host appends the viewer's AAD session hints (sid, login_hint)
 * to the embedded URL. MSAL caches in sessionStorage, so a freshly created
 * iframe never starts with an account - hard-wrapping the route in
 * MsalAuthenticationTemplate would fire an interactive popup on load, without
 * a user gesture, which browsers block.
 *
 * Resolution order: host token bridge (desktop/mobile hosts deliver a bearer
 * over postMessage, no MSAL at all - see mpdTokenBridge) -> cached account ->
 * ssoSilent with the host's hints -> interactive popup (last resort, same
 * behavior the route had before).
 */

const getHashQueryParams = (): URLSearchParams => {
  // HashRouter keeps the query inside the fragment: #/embedded-app/mpd?sid=...
  const hash = window.location.hash;
  const query = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
  return new URLSearchParams(query);
};

type Phase = "resolving" | "authenticated" | "interactive";

export function EmbeddedMsalSsoGate({
  children,
  loadingLabel = "Signing in...",
}: {
  children: React.ReactNode;
  loadingLabel?: string;
}) {
  const { instance } = useMsal();
  const [phase, setPhase] = useState<Phase>("resolving");
  // ssoSilent opens a hidden iframe; a second concurrent call throws
  // interaction_in_progress, so guard against StrictMode's double effect run.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // No cancellation on cleanup: with the started guard, StrictMode's
    // unmount/remount would otherwise cancel the only run and strand the
    // spinner. Late setState on this kept-alive component is harmless.
    const resolve = async () => {
      // A bridging host (PBS Live desktop/mobile) answers the ready handshake
      // with a bearer token and MSAL is never touched. No answer within the
      // handshake window means a non-bridging host (PBS Live web, standalone).
      const bridged = await waitForBridgeToken();
      if (bridged) {
        setPhase("authenticated");
        return;
      }

      const account =
        instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (account) {
        instance.setActiveAccount(account);
        setPhase("authenticated");
        return;
      }

      const params = getHashQueryParams();
      const sid = params.get("sid") ?? undefined;
      const loginHint = params.get("login_hint") ?? undefined;

      if (!sid && !loginHint) {
        setPhase("interactive");
        return;
      }

      try {
        const result = await instance.ssoSilent({
          scopes: MY_PROVIDER_DATA_SCOPES,
          sid,
          loginHint,
        });
        instance.setActiveAccount(result.account);
        setPhase("authenticated");
      } catch (error) {
        console.warn(
          "Silent SSO with host hints failed; falling back to interactive login.",
          error
        );
        setPhase("interactive");
      }
    };

    void resolve();
  }, [instance]);

  if (phase === "resolving") {
    return (
      <div className="text-center pt-12">
        <div className="p-5 flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          <span className="text-lg font-light">{loadingLabel}</span>
        </div>
      </div>
    );
  }

  if (phase === "authenticated") {
    return <>{children}</>;
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Popup}
      loadingComponent={SigningIn}
      errorComponent={SignInPrompt}
    >
      {children}
    </MsalAuthenticationTemplate>
  );
}

const SigningIn = () => (
  <div className="text-center pt-12">
    <div className="p-5 flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      <span className="text-lg font-light">Signing in...</span>
    </div>
  </div>
);

/**
 * The automatic popup has no user gesture behind it, so browsers block it and
 * the template lands here. Rendering a button gives the retry a real gesture,
 * which browsers allow.
 */
const SignInPrompt = () => {
  const { instance } = useMsal();

  return (
    <div className="text-center pt-12">
      <div className="p-5 flex flex-col items-center gap-4">
        <span className="text-lg font-light">
          Your session could not be verified automatically.
        </span>
        <button
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
          onClick={() =>
            instance
              .loginPopup({ scopes: MY_PROVIDER_DATA_SCOPES })
              .then((result) => instance.setActiveAccount(result.account))
              .catch((error) => console.warn("Interactive sign-in failed.", error))
          }
        >
          Sign in
        </button>
      </div>
    </div>
  );
};
